import styled from 'styled-components';
import { Button } from '@hqo/ui-atoms';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

/*
 * Lifted from the booking-details card when this moved out of that page: it is now rendered on the
 * receipt modal and the mobile sheet too, and a shared component reaching back into one page's
 * stylesheet is how those two drift apart.
 *
 * RES-1367: the answers' own heading has gone with it -- CollapsableBlock supplies one, now that
 * they fold.
 */
export const AnswerRow = styled.div`
  /* Between rows only: CollapsableBlock already spaces its content from the heading. */
  & + & {
    margin-top: 16px;
  }
`;

export const AnswerLabel = styled.div`
  font: 600 13px/1 ${({ theme }) => theme.fonts.join()};
  color: ${({ theme }) => theme.colors.palette.systemGreyDark};
`;

export const AnswerValue = styled.div`
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 8px;
  font: 400 15px/1.3 ${({ theme }) => theme.fonts.join()};
  color: ${({ theme }) => theme.colors.fontColor};
`;

/*
 * RES-1367: the order's actions row, and it is the SAME row as the booking card's one card above
 * (`BookingActionsRow` in pages/booking-details/styles.ts): 20px of air, a flex row, the trailing
 * button pushed to the card's edge. Two cards in one column whose footers are each other's
 * neighbours have to be built the same way or the pair reads as two different kinds of card.
 *
 * ⛔ NO RULE ABOVE IT, and it had one. A full-bleed border pulled out to the card's edges made this
 * footer a compartment of its own, which the details card -- the thing it sits directly beneath --
 * does not have; on screen the add-ons card looked like a dialog and the details card like a card.
 * The card's own edge is the boundary, and it is already drawn. The gutter-cancelling margins went
 * with the rule: there is nothing left to bleed.
 */
export const PayNowRow = styled.div`
  display: flex;
  align-items: center;
  /* The same 12px the booking card puts between check-in and cancel; see BookingActionsRow. */
  gap: 12px;
  margin-top: 20px;
`;

/*
 * 4px is 20 minus the 16px gap the card body already pays between children (`Content` in
 * components/collapsable-block/styles.ts, `card` variant), so the footer stands off by the same
 * 20px as `BookingActionsRow`. `ordered-add-ons.spec.tsx` reads both out of the emitted CSS and
 * fails if their sum stops matching.
 *
 * `PayNowRow` keeps its full 20px: the order-placed confirmation screen
 * (pages/booking-add-ons/components/add-ons-ordered.tsx) renders it in a plain block column with
 * no gap to borrow.
 */
export const OrderFooterRow = styled(PayNowRow)`
  margin-top: 4px;
`;

/*
 * The push only; the destructive colours come from ui-atoms' `secondaryDestructive` variant at the
 * call site, matching the booking card's `CancelActionButton`.
 *
 * The push is the trailing button's rather than the row's, and only one button may ask for it:
 * `margin-left: auto` claims a share of the free space before each element that asks, so two
 * asking buttons split the gap. `justify-content: space-between` on the row is wrong for the same
 * reason in reverse -- a footer holding only this button would put it at the leading edge.
 */
export const CancelOrderButton = styled(Button)`
  margin-left: auto;
`;

/*
 * RES-1367 -- the offer box (see ./add-ons-offer.tsx).
 *
 * DASHED rather than solid, which is the whole point of the treatment: it is the one thing in this
 * column that is not a record of the booking, and a solid card would read as one more of them.
 * Borrowed from activity center's end-of-list CTA, which makes the same distinction for the same
 * reason.
 */
export const OfferBox = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 20px;
  border: 1px dashed ${({ theme }) => theme.colors.palette.systemGreyMedium};
  border-radius: 12px;
  text-align: center;
`;

export const OfferText = styled.p`
  margin: 0;
  font: 400 14px/1.4 ${({ theme }) => theme.fonts.join()};
  color: ${({ theme }) => theme.colors.palette.systemGreyDark};
`;

/*
 * The icon needs a styled wrapper: FontAwesome ships `.svg-inline--fa { height: 1em }` unlayered,
 * which beats ui-atoms' Tailwind sizing, so neither the variant's `size-4` nor a `size-[13px]`
 * passed on the icon would land.
 */
export const OfferButtonIcon = styled(FontAwesomeIcon)`
  width: 13px;
  height: 13px;
`;

/*
 * Expands in place of `OrderFooterRow`, so it takes that row's 4px -- the card body pays 16 of the
 * 20. The booking card one card above confirms its own cancel the same way.
 *
 * Focused programmatically as it opens (see the call site) and never reachable by Tab, so the ring
 * that focus would draw is suppressed.
 */
export const CancelConfirmSection = styled.div`
  margin-top: 4px;
  display: flex;
  flex-direction: column;
  gap: 12px;

  &:focus {
    outline: none;
  }
`;

export const CancelConfirmText = styled.div`
  font: 400 14px/1.4 ${({ theme }) => theme.fonts.join()};
  color: ${({ theme }) => theme.colors.fontColor};
`;

export const CancelConfirmButtonsRow = styled.div`
  display: flex;
  gap: 12px;
`;
